import React from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/Button';
import { missingScopesText } from '../../features/stockSync/stockSyncUtils';

const BOX =
  'rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300';

/**
 * What stands between a Shopify store and the stock sync, above the dialog
 * that would use it. A store that never granted the permissions has to
 * authorize Ninja again: its owner does it from here (`onReauthorize`); whoever
 * runs the warehouse is told to ask them.
 *
 * @param {object} props
 * @param {{kind: 'missing', scopes: string[]}|{kind: 'unanswered', error: string}|null} props.problem -
 *   `scopesProblemOf`.
 * @param {string} props.companyName - The client the store belongs to.
 * @param {() => void} [props.onReauthorize] - Only for the store's owner.
 * @param {boolean} [props.reauthorizing=false]
 * @param {string|null} [props.error] - Why the last attempt to authorize failed.
 * @param {boolean} [props.forComparison=false] - The permissions only comparing the stock needs
 *   (`alignmentScopesProblemOf`): the rest of the sync still works.
 */
export const ShopifyScopesNotice = ({
  problem,
  companyName,
  onReauthorize,
  reauthorizing = false,
  error = null,
  forComparison = false,
}) => {
  if (!problem) return null;

  if (problem.kind === 'unanswered') {
    return (
      <div data-testid="shopify-scopes-notice" data-kind="unanswered" className={BOX}>
        <p className="flex items-start gap-2 font-semibold">
          <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
          La tienda no respondió. Prueba de nuevo en unos minutos.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="shopify-scopes-notice"
      data-kind={forComparison ? 'alignment' : 'missing'}
      className={`${BOX} space-y-3`}
    >
      <p className="flex items-start gap-2 font-semibold">
        <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
        {missingScopesText(problem.scopes, { forComparison })}
      </p>
      {onReauthorize ? (
        <Button
          variant="outline"
          icon={ShieldCheck}
          loading={reauthorizing}
          loadingLabel="Abriendo Shopify…"
          onClick={onReauthorize}
          data-testid="shopify-reauthorize"
        >
          Volver a autorizar
        </Button>
      ) : (
        <p>Pídele a {companyName} que vuelva a autorizar su tienda en Ninja.</p>
      )}
      {error && (
        <p data-testid="shopify-reauthorize-error" className="font-semibold text-danger">
          {error}
        </p>
      )}
    </div>
  );
};
